import type {
  AppContext,
  Navigation,
  Route,
  ViewCleanup,
  ViewMount,
} from "./app";
import type { GlobeController } from "./map/world-globe";
import { mountRegionPicker } from "./views/region-picker";
import { mountLearnSession } from "./views/learn";
import { mountRegionCleared } from "./views/region-cleared";
import { mountReview } from "./views/review";
import { mountSettings } from "./views/settings";
import { mountHeatmap } from "./views/heatmap";
import { mountArcadeHub } from "./views/arcade-hub";
import { mountArcadeMode } from "./views/arcade-mode";
import { mountBrowse } from "./views/browse";
import { mountLeaderboard } from "./views/leaderboard";

const mounts: Record<Route["type"], ViewMount> = {
  "region-picker": mountRegionPicker,
  learn: mountLearnSession,
  "region-cleared": mountRegionCleared,
  review: mountReview,
  settings: mountSettings,
  heatmap: mountHeatmap,
  arcade: mountArcadeHub,
  "arcade-mode": mountArcadeMode,
  browse: mountBrowse,
  leaderboard: mountLeaderboard,
};

export const startRouter = (
  viewLayer: HTMLElement,
  globe: GlobeController,
): void => {
  let cleanup: ViewCleanup | null = null;
  let pendingNav: Route | null = null;
  let mountInFlight = false;

  const ctx: AppContext = {
    globe,
    nav: {} as Navigation,
  };

  const go = async (route: Route): Promise<void> => {
    pendingNav = route;
    if (mountInFlight) return;
    mountInFlight = true;
    try {
      while (pendingNav) {
        const next = pendingNav;
        pendingNav = null;
        if (cleanup) {
          cleanup();
          cleanup = null;
        }
        viewLayer.replaceChildren();
        // Clean previous route's data-* attrs so stale values don't leak.
        delete viewLayer.dataset.region;
        delete viewLayer.dataset.missedIsos;
        delete viewLayer.dataset.mode;
        const mount = mounts[next.type];
        if (next.type === "learn") viewLayer.dataset.region = next.region;
        if (next.type === "region-cleared")
          viewLayer.dataset.region = next.region;
        if (next.type === "review") {
          viewLayer.dataset.region = next.region;
          viewLayer.dataset.missedIsos = next.missedIsos.join(",");
        }
        if (next.type === "arcade-mode") {
          viewLayer.dataset.region = next.region;
          viewLayer.dataset.mode = next.mode;
        }
        if (next.type === "browse" && next.region) {
          viewLayer.dataset.region = next.region;
        }
        if (next.type === "leaderboard") {
          if (next.mode) viewLayer.dataset.mode = next.mode;
          if (next.region) viewLayer.dataset.region = next.region;
        }
        const result = mount(viewLayer, ctx);
        cleanup = result instanceof Promise ? await result : result;
      }
    } finally {
      mountInFlight = false;
    }
  };

  ctx.nav.goRegionPicker = () => void go({ type: "region-picker" });
  ctx.nav.goLearn = ({ region }) => void go({ type: "learn", region });
  ctx.nav.goRegionCleared = ({ region }) =>
    void go({ type: "region-cleared", region });
  ctx.nav.goReview = ({ region, missedIsos }) =>
    void go({ type: "review", region, missedIsos });
  ctx.nav.goSettings = () => void go({ type: "settings" });
  ctx.nav.goHeatmap = () => void go({ type: "heatmap" });
  ctx.nav.goArcade = () => void go({ type: "arcade" });
  ctx.nav.goArcadeMode = ({ mode, region }) =>
    void go({ type: "arcade-mode", mode, region });
  ctx.nav.goBrowse = (opts) =>
    void go({ type: "browse", region: opts?.region ?? null });
  ctx.nav.goLeaderboard = (opts) =>
    void go({
      type: "leaderboard",
      mode: opts?.mode ?? null,
      region: opts?.region ?? null,
    });

  void go({ type: "region-picker" });
};
